import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Table from '../../components/Table';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface ExcludeDaysRow {
  id: string;
  code: string;
  name: string;
}

export default function ExcludeDays() {
  const [rows, setRows] = useState<ExcludeDaysRow[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [codeName, setCodeName] = useState({ code: '', name: '' });
  const [deleteTarget, setDeleteTarget] = useState<ExcludeDaysRow | null>(null);
  const [weekdaysTarget, setWeekdaysTarget] = useState<ExcludeDaysRow | null>(null);
  const [weekdayMap, setWeekdayMap] = useState<Record<number, 'FULL' | 'HALF' | ''>>({});
  const [error, setError] = useState('');

  const load = async () => {
    const res = await api.get('/lms/masters/exclude-days');
    setRows(res.data.excludeDayss || []);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setCodeName({ code: '', name: '' });
    setError('');
    setShowForm(true);
  };

  const openEdit = (row: ExcludeDaysRow) => {
    setEditingId(row.id);
    setCodeName({ code: row.code, name: row.name });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await api.put(`/lms/masters/exclude-days/${editingId}`, codeName);
      } else {
        await api.post('/lms/masters/exclude-days', codeName);
      }
      setShowForm(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await api.delete(`/lms/masters/exclude-days/${deleteTarget.id}`);
    setDeleteTarget(null);
    load();
  };

  const openWeekdays = async (row: ExcludeDaysRow) => {
    setWeekdaysTarget(row);
    const res = await api.get(`/lms/masters/exclude-days/${row.id}/weekdays`);
    const map: Record<number, 'FULL' | 'HALF' | ''> = {};
    for (let i = 0; i < 7; i++) map[i] = '';
    for (const w of res.data.weekdays || []) map[w.weekday] = w.exclusion;
    setWeekdayMap(map);
  };

  const saveWeekdays = async () => {
    if (!weekdaysTarget) return;
    const weekdays = Object.entries(weekdayMap)
      .filter(([, v]) => v)
      .map(([weekday, exclusion]) => ({ weekday: Number(weekday), exclusion }));
    await api.put(`/lms/masters/exclude-days/${weekdaysTarget.id}/weekdays`, { weekdays });
    setWeekdaysTarget(null);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">Exclude Days</h1>
        <Button onClick={openCreate}>+ New</Button>
      </div>
      <Table
        columns={[
          { key: 'code', header: 'Code' },
          { key: 'name', header: 'Name' },
          {
            key: 'actions', header: 'Actions',
            render: (row) => (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => openWeekdays(row)}>Weekdays</Button>
                <Button variant="ghost" onClick={() => openEdit(row)}>Edit</Button>
                <Button variant="ghost" className="text-red-600" onClick={() => setDeleteTarget(row)}>Delete</Button>
              </div>
            ),
          },
        ]}
        rows={rows}
      />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? 'Edit Exclude Days' : 'New Exclude Days'}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <Input label="Code" maxLength={6} value={codeName.code} onChange={(e) => setCodeName({ ...codeName, code: e.target.value })} required />
          <Input label="Name" maxLength={50} value={codeName.name} onChange={(e) => setCodeName({ ...codeName, name: e.target.value })} required />
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit">Save</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!weekdaysTarget} onClose={() => setWeekdaysTarget(null)} title={`Weekday Exclusions — ${weekdaysTarget?.name}`}>
        <div className="space-y-3">
          {WEEKDAYS.map((day, idx) => (
            <div key={idx} className="flex items-center justify-between">
              <span className="text-sm text-gray-700">{day}</span>
              <select
                className="border rounded px-2 py-1 text-sm"
                value={weekdayMap[idx] || ''}
                onChange={(e) => setWeekdayMap({ ...weekdayMap, [idx]: e.target.value as any })}
              >
                <option value="">Not Excluded</option>
                <option value="FULL">Full</option>
                <option value="HALF">Half</option>
              </select>
            </div>
          ))}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setWeekdaysTarget(null)}>Cancel</Button>
            <Button onClick={saveWeekdays}>Save</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete"
        message={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
